import { useFormContext } from 'react-hook-form';
import { useUserRoles } from '@anytag/auth';
import {
  EngagementDetail,
  SocialAccountType,
  CampaignSocialMediaType,
  useCampaignHashtagCloudLazyQuery
} from '@anytag/graphql';
import { Drawer, Form } from '@anytag/shared-atoms';
import { UNEXPECTED_ERROR, UPLOAD_IMAGE_MIME } from '@anytag/shared-constants';
import { useQueryHelper } from '@anytag/shared-hooks';
import {
  TextField,
  TextareaField,
  FileUploadField,
  SingleDatePickerField,
  HashtagSuggestionField
} from '@anytag/shared-molecules';
import { EngagementPostFormKeys, EngagementPostFormValues } from '../types';
import { FooterButtons, InfluencerAccountFields } from './shared';

interface DefaultTemplateProps {
  onClose: () => void;
  onSubmit?: () => void;
  hasPostMissingMetricsData: boolean;
  engagementId: EngagementDetail['id'];
  isTtcmCampaign: EngagementDetail['isTtcmCampaign'];
}
export const DefaultTemplate = ({
  onClose,
  onSubmit,
  engagementId,
  isTtcmCampaign,
  hasPostMissingMetricsData
}: DefaultTemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { isAdminStaff, isAdvertiser, isPartner } = useUserRoles();
  const { setValue, watch } = useFormContext<EngagementPostFormValues>();
  const { data, getCampaignHashtagCloud } = useCampaignHashtagCloudLazyQuery();

  const hashtagsData = data?.campaignHashtagCloud;
  const [contentValue, socialAccountMediaValue, socialPostMediaValue] = watch([
    'content',
    'socialAccountMedia',
    'socialPostMedia'
  ]);

  const isFacebookPost = socialPostMediaValue === CampaignSocialMediaType.FACEBOOK;
  const isTwitterPost = socialPostMediaValue === CampaignSocialMediaType.TWITTER;
  const isYoutubePost = socialPostMediaValue === CampaignSocialMediaType.YOUTUBE;
  const isTikTokPost = socialPostMediaValue === CampaignSocialMediaType.TIKTOK;
  const isInstagramPost =
    !!socialPostMediaValue &&
    [CampaignSocialMediaType.INSTAGRAM, CampaignSocialMediaType.INSTAGRAM_STORY].includes(socialPostMediaValue);

  const handleFetchCampaignHashtagCloud = async () => {
    try {
      const tagsForAPI = contentValue?.split('#') || [''];
      await getCampaignHashtagCloud({
        variables: {
          ...(!!tagsForAPI.length && { tags: tagsForAPI }),
          ...(!!socialAccountMediaValue && {
            socialMedia:
              socialAccountMediaValue === SocialAccountType.FACEBOOK_PAGE
                ? CampaignSocialMediaType.FACEBOOK
                : (socialAccountMediaValue as unknown as CampaignSocialMediaType)
          })
        }
      });
    } catch (error) {
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  return (
    <>
      <Drawer.Body>
        <Form.SectionTitle index={1} label={t('Influencer.Post Information')} />

        <InfluencerAccountFields engagementId={engagementId} isTtcmCampaign={isTtcmCampaign} />

        <TextField<EngagementPostFormKeys>
          name="postUrl"
          disabled={isAdvertiser}
          title={t('TextForm.Post URL')}
          placeholder="https://www.instagram.com/p/BpYTz-JA0g3/"
        />

        <TextareaField<EngagementPostFormKeys>
          name="content"
          disabled={isAdvertiser}
          title={t('TextForm.Contents')}
          help={t('Tooltip.Post Content Help')}
          onBlur={handleFetchCampaignHashtagCloud}
          placeholder={t<string>('TextForm.Contents Placeholder')}
        />

        {hashtagsData?.length ? (
          <HashtagSuggestionField
            name="hashtags" // Any name, we don't use this name in form
            hasHashTagForm={false}
            hashtagsCloud={hashtagsData}
            getHashtagsCloud={handleFetchCampaignHashtagCloud}
            onTagClick={(value) => {
              setValue('content', `${contentValue} ${value}`);
              handleFetchCampaignHashtagCloud();
            }}
          />
        ) : null}

        <FileUploadField<EngagementPostFormKeys>
          target="post"
          itemsPerRow={3}
          name="materialsUrl"
          title={t('TextForm.Files')}
          help={t('Tooltip.Files Help')}
          multiple
        />

        {isAdminStaff || isPartner ? (
          <>
            <Form.SectionTitle index={2} className="mt-3" label={t('Insight Upload')} />

            <div className="flex items-center justify-between">
              <p className="text-base leading-[100%]">{t('Date of data acquisition for insights')}</p>
              <SingleDatePickerField<EngagementPostFormKeys>
                name="insightDataAcquisition"
                className="max-w-[157px] p-0"
                withinPortal={false}
              />
            </div>

            <FileUploadField<EngagementPostFormKeys>
              hasZoomPreview
              target="post"
              itemsPerRow={3}
              name="insightUrls"
              notes={[
                t('General.DragAndDropInfo'),
                t('General.DragAndDropMaxSize', { maxSize: '10MB' }),
                t('General.DragAndDropMIME', { MIME: UPLOAD_IMAGE_MIME })
              ]}
              multiple
            />
          </>
        ) : null}

        {hasPostMissingMetricsData ? (
          <>
            <Form.SectionTitle index={3} className="mt-3" label={t('Result')} />

            <Form.Fields itemsPerRow={2}>
              <TextField<EngagementPostFormKeys>
                type="number"
                name="likes"
                title={t(isFacebookPost ? 'TextForm.Like Reactions' : 'TextForm.Likes')}
                placeholder="0"
              />
              {isYoutubePost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="dislikes"
                  title={t('TextForm.Dislike')}
                  placeholder="0"
                />
              ) : null}
              <TextField<EngagementPostFormKeys>
                type="number"
                name="comments"
                title={t('TextForm.Comments')}
                placeholder="0"
              />
              {isInstagramPost || isFacebookPost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="reach"
                  title={t('TextForm.Reach')}
                  placeholder="0"
                />
              ) : null}
              {!isYoutubePost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="saves"
                  title={t('TextForm.Save')}
                  placeholder="0"
                />
              ) : null}
              {isTikTokPost || isFacebookPost || isTwitterPost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="shares"
                  title={t(isTwitterPost ? 'TextForm.Retweets' : 'TextForm.Shares')}
                  placeholder="0"
                />
              ) : null}
              {isFacebookPost || isYoutubePost || isTwitterPost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="impressions"
                  title={t('TextForm.Impression')}
                  placeholder="0"
                />
              ) : null}
              {!isTwitterPost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="views"
                  title={t('TextForm.Video Views')}
                  placeholder="0"
                />
              ) : null}
              {isTwitterPost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="detailExpands"
                  title={t('TextForm.Detail Expands')}
                  placeholder="0"
                />
              ) : null}
              {isYoutubePost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="impressionsClickThroughRate"
                  title={t('TextForm.Impressions click-through rate')}
                  placeholder="0"
                  rightSection="%"
                />
              ) : null}
              {isTikTokPost ? (
                <>
                  <TextField<EngagementPostFormKeys>
                    type="number"
                    name="totalPlayTime"
                    title={t('TextForm.Total Play Time')}
                    placeholder="0"
                  />
                  <TextField<EngagementPostFormKeys>
                    type="number"
                    name="fullVideoViews"
                    title={t('TextForm.Watched Full Video')}
                    placeholder="0"
                  />
                </>
              ) : null}
              {isTikTokPost || isYoutubePost ? (
                <TextField<EngagementPostFormKeys>
                  type="number"
                  name="avgWatchTimes"
                  title={t('TextForm.Average Watch Time')}
                  placeholder="0"
                />
              ) : null}
              {isYoutubePost ? (
                <>
                  <TextField<EngagementPostFormKeys>
                    type="number"
                    name="avgPlayRate"
                    title={t('TextForm.Average Play Rate')}
                    placeholder="0"
                    rightSection="%"
                  />
                  <TextField<EngagementPostFormKeys>
                    type="number"
                    name="audienceRetention"
                    title={t('TextForm.View retention rate')}
                    placeholder="0"
                    rightSection="%"
                  />
                </>
              ) : null}
            </Form.Fields>
          </>
        ) : null}
      </Drawer.Body>
      <Drawer.Footer>
        <FooterButtons onClose={onClose} onSubmit={onSubmit} />
      </Drawer.Footer>
    </>
  );
};
